import React, { useEffect, useMemo, useState } from 'react';
import swal from 'sweetalert';
import LoginHeaderOperational from './LoginHeaderOperational';
import Sidebar_Progress from '../layouts/Operational_Sidebar_Progress.component';
import { API_URL } from '../../constant/API_Settings';
import { fetchReportJson, reportNumberFormat, downloadReportPdf, formatReportDate, formatReportDateRange } from '../../utils/reportFormat';
import logo from '../../img/logo.svg';
import {
  OtherReportActionButtons,
  OtherReportFilterCard,
  OtherReportFilterSection,
  ReportDateRangeFields,
  OtherReportLoader,
  OtherReportPageHeader,
  OtherReportResultsZone,
  OtherReportSearchableCheckList,
  OtherReportShell,
  OtherReportToolbar,
} from './other-reports/OtherReportShared';

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const NonFuelPurchaseReport = (props) => {
  const [meta, setMeta] = useState({ vendors: [], products: [] });
  const [inputValues, setInputValues] = useState({ vendor: [], product: [] });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const buildPayload = () => ({
    from_date: inputValues.start_date,
    to_date: inputValues.end_date,
    vendor: inputValues.vendor,
    product: inputValues.product,
  });

  const validateFilters = () => {
    if (!inputValues.start_date || !inputValues.end_date) {
      swal('Please select start and end dates', '', 'warning');
      return false;
    }
    return true;
  };

  useEffect(() => {
    fetchReportJson(`${API_URL}/product_purchase_statement/meta`)
      .then(setMeta)
      .catch((error) => swal('Non-Fuel Purchase', error.message, 'error'));
  }, []);

  const toggleSelection = (field, id) => {
    const selected = new Set((inputValues[field] || []).map(String));
    const key = String(id);
    if (selected.has(key)) selected.delete(key);
    else selected.add(key);
    setInputValues({ ...inputValues, [field]: Array.from(selected) });
  };

  const setSelection = (field, ids) => {
    setInputValues({ ...inputValues, [field]: (ids || []).map(String) });
  };

  const loadReport = async () => {
    if (!validateFilters()) {
      return;
    }

    setLoading(true);
    setReport(null);

    try {
      setReport(await fetchReportJson(`${API_URL}/product_purchase_statement`, buildPayload()));
    } catch (error) {
      swal('Non-Fuel Purchase', error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    if (!validateFilters()) {
      return;
    }

    setPdfLoading(true);
    try {
      await downloadReportPdf(
        `${API_URL}/product_purchase_statement/pdf`,
        buildPayload(),
        'product_purchase_statement.pdf',
      );
    } catch (error) {
      swal('Non-Fuel Purchase', error.message, 'error');
    } finally {
      setPdfLoading(false);
    }
  };

  const totalsRow = useMemo(() => {
    if (report?.totals_row) {
      return report.totals_row;
    }
    const rows = report?.rows || [];
    return {
      label: 'Total',
      quantity: rows.reduce((sum, row) => sum + toNumber(row.quantity), 0),
      pp_per_piece_exc_gst: rows.reduce((sum, row) => sum + toNumber(row.pp_per_piece_exc_gst), 0),
      gst_amount: rows.reduce((sum, row) => sum + toNumber(row.gst_amount), 0),
      amount: rows.reduce((sum, row) => sum + toNumber(row.amount), 0),
    };
  }, [report]);

  return (
    <div className="main-container page-wrap">
      <div className="d-md-flex">
        <Sidebar_Progress props={props} progress={14} operation={3} op={3} />
        <div className="content-area">
          <LoginHeaderOperational props={props} page_title=" " />
          <OtherReportShell>
            <OtherReportPageHeader title="Non-Fuel Purchase Statement" badge="Reports" />
            <OtherReportFilterCard>
              <OtherReportToolbar
                actionsWide
                dates={(
                  <>
                    <ReportDateRangeFields
                      startDate={inputValues.start_date}
                      endDate={inputValues.end_date}
                      onChange={({ start_date, end_date }) => setInputValues({
                        ...inputValues,
                        start_date,
                        end_date,
                      })}
                    />
                  </>
                )}
                actions={(
                  <OtherReportActionButtons
                    loading={loading}
                    pdfLoading={pdfLoading}
                    onSubmit={loadReport}
                    onDownloadPdf={downloadPdf}
                  />
                )}
              />
              <OtherReportFilterSection title="Optional filters" description="Narrow results by vendor or product.">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="other-report-filter-section-title d-block mb-2">Vendors</label>
                    <OtherReportSearchableCheckList
                      items={meta.vendors || []}
                      selectedIds={inputValues.vendor || []}
                      onToggle={(id) => toggleSelection('vendor', id)}
                      onChange={(ids) => setSelection('vendor', ids)}
                      searchPlaceholder="Search vendors…"
                      getId={(item) => String(item.id)}
                      getLabel={(item) => item.name}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="other-report-filter-section-title d-block mb-2">Products</label>
                    <OtherReportSearchableCheckList
                      items={meta.products || []}
                      selectedIds={inputValues.product || []}
                      onToggle={(id) => toggleSelection('product', id)}
                      onChange={(ids) => setSelection('product', ids)}
                      searchPlaceholder="Search products…"
                      getId={(item) => String(item.id)}
                      getLabel={(item) => item.product_name}
                    />
                  </div>
                </div>
              </OtherReportFilterSection>
            </OtherReportFilterCard>
            <OtherReportResultsZone>
              {loading && <OtherReportLoader message="Generating purchase statement" />}
              {report && !loading && (
              <div className="form-wrap balace-sheetwrap">
                <div className="row mb-3">
                  <div className="col-md">
                    <h2 className="table-title mb-1">{report.pump_name}</h2>
                    <h4 className="table-title mb-1">{report.type}</h4>
                    <h5>{formatReportDateRange(report.start_date, report.end_date)}</h5>
                    <img src={logo} className="img-fluid float-end" width="100" alt="logo" />
                  </div>
                </div>
                <div className="other-report-table-wrap">
                <table className="other-report-table">
                  <thead>
                    <tr>
                      <th>Date</th><th>Invoice no</th><th>Invoice amount</th><th>Vendor</th>
                      <th>Product</th><th>HSN</th><th>Qty</th><th>Price exc GST</th><th>GST</th><th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(report.rows || []).map((row, i, arr) => {
                      const showHeader = i === 0 || arr[i - 1].nf_purchase_id !== row.nf_purchase_id;
                      return (
                        <tr key={`row-${i}`}>
                          <td>{showHeader ? formatReportDate(row.invoice_date) : ''}</td>
                          <td>{showHeader ? (row.invoice_number || '—') : ''}</td>
                          <td>{showHeader ? reportNumberFormat(row.invoice_amount ?? row.net_amount) : ''}</td>
                          <td>{showHeader ? row.vendor : ''}</td>
                          <td>{row.product_name}</td>
                          <td>{row.hsn_code}</td>
                          <td className="report-amount">{row.quantity}</td>
                          <td className="report-amount">{reportNumberFormat(row.pp_per_piece_exc_gst)}</td>
                          <td className="report-amount">{reportNumberFormat(row.gst_amount)}</td>
                          <td className="report-amount">{reportNumberFormat(row.amount)}</td>
                        </tr>
                      );
                    })}
                    {(report.rows || []).length > 0 && (
                      <tr className="other-report-totals-row">
                        <td colSpan={6}>{totalsRow.label || 'Total'}</td>
                        <td className="report-amount">{totalsRow.quantity}</td>
                        <td className="report-amount">{reportNumberFormat(totalsRow.pp_per_piece_exc_gst)}</td>
                        <td className="report-amount">{reportNumberFormat(totalsRow.gst_amount)}</td>
                        <td className="report-amount">{reportNumberFormat(totalsRow.amount)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
                </div>
              </div>
              )}
            </OtherReportResultsZone>
          </OtherReportShell>
        </div>
      </div>
    </div>
  );
};

export default NonFuelPurchaseReport;
